// Frontend-only mock data + helpers for assessor star ratings and public profiles.
// Ratings/profiles are derived deterministically from the lot so they stay stable
// between renders. Replace with real API data (e.g. lot.assessor / Api.getAssessor)
// when the backend is ready.

function hashString (str) {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0
  }
  return hash
}

function slugify (name) {
  return String(name).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

function lotKey (lot) {
  if (!lot) return 'default'
  return String(
    lot._id ||
    lot.searchNumericId ||
    (lot.creatorData && lot.creatorData.tradingName) ||
    lot.title ||
    'default'
  )
}

// Deterministic decimal rating in the range 3.5 - 5.0 (0.1 steps).
export function getAssessorRating (lot) {
  const hash = hashString(lotKey(lot))
  return Math.round((3.5 + (hash % 16) / 10) * 10) / 10
}

export function getReviewsCount (lot) {
  return 12 + (hashString(lotKey(lot)) % 40)
}

// Resolve the assessor (agent or vendor assessor) for a lot.
export function getAssessorInfo (lot) {
  const creator = (lot && lot.creatorData) || {}
  const name =
    creator.assessorName ||
    creator.tradingName ||
    creator.name ||
    'Simon Williams'
  return { id: slugify(name), name }
}

const namedProfiles = {
  'kate-gooden': {
    id: 'kate-gooden',
    name: 'Kate Gooden',
    tradingName: 'Great Goats',
    bio: 'I have been a producer for 18 years. I am a well respected agent you should use me. I am really good at assessing goats, ducks and emus.',
    phone: '0488 443 550',
    email: 'kgooden@farmgateauctions.com.au',
    location: 'Cootamundra, NSW 2590',
    joined: '2011',
    assessmentsCompleted: 148,
    document: 'Certificate of Vendor Assessor',
    rating: 4.87,
    reviews: 35,
    breakdown: [
      { label: 'Weight', value: 4.9 },
      { label: 'Breeding Details', value: 4.8 },
      { label: 'Condition & Frame', value: 4.7 },
      { label: 'Temperament', value: 4.6 },
      { label: 'Blemishes', value: 4.1 }
    ],
    upcomingListings: [
      '14 Angus Heifer Weaners 15-16 mths',
      '81 Angus cross Black Baldy Steer Yearlings 10-11 mths',
      '35 Hereford Cow(s) and Calve(s) 4-5 yrs'
    ],
    pastListings: [
      '17 Angus Steer Yearlings 10-11 mths',
      '49 Hereford Cow(s) and Calve(s) 4-5 yrs'
    ]
  },
  'simon-williams': {
    id: 'simon-williams',
    name: 'Simon Williams',
    tradingName: 'Williams Livestock',
    bio: 'Third generation cattle producer with over 20 years assessing cattle and sheep across NSW and QLD. Known for accurate, honest assessments.',
    phone: '0412 998 210',
    email: 'swilliams@farmgateauctions.com.au',
    location: 'Tamworth, NSW 2340',
    joined: '2014',
    assessmentsCompleted: 96,
    document: 'Certificate of Vendor Assessor',
    rating: 4.72,
    reviews: 28,
    breakdown: [
      { label: 'Weight', value: 4.8 },
      { label: 'Breeding Details', value: 4.7 },
      { label: 'Condition & Frame', value: 4.6 },
      { label: 'Temperament', value: 4.9 },
      { label: 'Blemishes', value: 4.4 }
    ],
    upcomingListings: [
      '6 Limousin Heifer Vealers 8-9 mths',
      '22 Angus Steer Weaners 9-10 mths'
    ],
    pastListings: [
      '18 Charolais cross Steer Yearlings 12-13 mths',
      '40 Merino Ewes 3-4 yrs'
    ]
  }
}

function buildDefaultProfile (id, name) {
  const hash = hashString(id)
  return {
    id,
    name,
    tradingName: name,
    bio: `${name} is an accredited FarmGate assessor with a strong track record of accurate livestock assessments.`,
    phone: '04' + (10000000 + (hash % 89999999)),
    email: `${id.replace(/-/g, '')}@farmgateauctions.com.au`,
    location: 'NSW',
    joined: String(2010 + (hash % 12)),
    assessmentsCompleted: 20 + (hash % 180),
    document: 'Certificate of Vendor Assessor',
    rating: Math.round((3.8 + (hash % 12) / 10) * 100) / 100,
    reviews: 10 + (hash % 50),
    breakdown: [
      { label: 'Weight', value: 4.0 + (hash % 10) / 10 },
      { label: 'Breeding Details', value: 4.0 + ((hash >> 2) % 10) / 10 },
      { label: 'Condition & Frame', value: 4.0 + ((hash >> 4) % 10) / 10 },
      { label: 'Temperament', value: 4.0 + ((hash >> 6) % 10) / 10 },
      { label: 'Blemishes', value: 3.8 + ((hash >> 8) % 12) / 10 }
    ],
    upcomingListings: ['View this assessor’s current listings in Upcoming Auctions'],
    pastListings: ['View this assessor’s past listings in Results']
  }
}

export function getAssessorProfile (id) {
  if (namedProfiles[id]) {
    return namedProfiles[id]
  }
  const name = String(id || 'assessor')
    .split('-')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
  return buildDefaultProfile(id || 'assessor', name)
}
